/**
 * PromptCardGrid · 推荐分类项卡片容器（EmptyState 与 AISidebar QuickPrompts 共用）
 *
 * 两种 layout：
 *  · grid（默认）：2 列卡片，每张 = icon + 主标题 + 副标题
 *    · 奇数项自动丢最后一项（trimForTwoColGrid），避免末行孤悬
 *    · 用于建夹欢迎页（容器宽 ≥ 500px）
 *  · list：1 列竖列表，每张 = icon + 主标题 + 副标题 + 右箭头
 *    · 任意项数都不裁剪
 *    · 用于 AI 助手侧栏（容器宽 ~360px，2 列会挤）
 *
 * 公共行为：
 *  · loading 时显 4 张骨架卡（按 layout 自适应）
 *  · prompts 为空时渲染 FALLBACK_RECOMMENDED_PROMPTS（永远有可点入口）
 *
 * 数据契约：
 *  · prompts / loading 一般由父级 useAppSelector(selectInitRecommended / selectInitLoading) 注入
 *  · onSelect 在用户点击卡片时被调（传卡片 text，父级负责 sendUserMessage / openSidebar 等）
 */

import React from 'react'
import { ArrowRightOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import {
  RecommendedPrompt,
  FALLBACK_RECOMMENDED_PROMPTS,
  iconTypeToAntdIcon,
  trimForTwoColGrid
} from '../../utils/recommendedPrompt'
import styles from './PromptCardGrid.module.scss'

const SKELETON_COUNT = 4

export type PromptCardLayout = 'grid' | 'list'

export interface PromptCardGridProps {
  /** /init recommended_prompts 数据（空数组时走 FALLBACK） */
  prompts: RecommendedPrompt[]
  /** init 接口是否还在飞行中 · true 时显骨架 */
  loading: boolean
  /** 点击卡片时回调 · 传卡片 text（父级负责 sendUserMessage / openSidebar 等） */
  onSelect: (text: string) => void
  /** 是否禁用（如 SSE streaming 中） */
  disabled?: boolean
  /** 透传外层 className（父级若要给容器加额外间距 / 约束用） */
  className?: string
  /** 布局变体，默认 'grid' （2 列卡片）；'list' = 1 列竖列表 + 右箭头 */
  layout?: PromptCardLayout
}

const PromptCardGrid: React.FC<PromptCardGridProps> = ({
  prompts,
  loading,
  onSelect,
  disabled,
  className,
  layout = 'grid'
}) => {
  const isList = layout === 'list'
  const containerClass = classNames(
    isList ? styles.list : styles.grid,
    className
  )

  if (loading) {
    return (
      <div className={containerClass} role="group" aria-label="加载中">
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <span key={i} className={styles.skeleton} aria-hidden="true" />
        ))}
      </div>
    )
  }

  // list 不需要奇数裁剪（任何行数都正常）；grid 才需要
  const items =
    prompts.length > 0
      ? isList
        ? prompts
        : trimForTwoColGrid(prompts)
      : FALLBACK_RECOMMENDED_PROMPTS

  return (
    <div className={containerClass} role="list" aria-label="推荐分类方式">
      {items.map((opt) => {
        const PromptIcon = iconTypeToAntdIcon(opt.iconType)
        return (
          <button
            key={opt.text}
            type="button"
            role="listitem"
            className={styles.card}
            disabled={disabled}
            onClick={() => onSelect(opt.text)}
          >
            <span className={styles.cardIcon} aria-hidden="true">
              <PromptIcon />
            </span>
            <span className={styles.cardText}>
              <span className={styles.cardTitle}>{opt.text}</span>
              {opt.subtitle && (
                <span className={styles.cardSubtitle}>{opt.subtitle}</span>
              )}
            </span>
            {isList && (
              <ArrowRightOutlined
                className={styles.cardArrow}
                aria-hidden="true"
              />
            )}
          </button>
        )
      })}
    </div>
  )
}

export default PromptCardGrid
